

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const HearingWellness = () => {
  // State for form data
  const [date, setDate] = useState(''); // Placeholder for date state
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [downloadFormat, setDownloadFormat] = useState('excel');
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    phone: '',
    opNumber: '',
    occupation: '',
    education: '',
    maritalStatus: '',
    address: '',
    area: '',
    socialization: '',
    socioEconomicStatus: '',
    systemicIllnesses: [],
    otherSystemicIllness: '',
    selfReportedHearingDifficulties: '',
    hearingLoss: '',
    hearingAid: '',
    questionnaires: {
      hcaq: { result: '', score: '' },
      scap: { result: '', score: '' },
      mmse: { result: '', score: '' },
      moca: {
        visospatial: '',
        naming: '',
        memory: '',
        attention: '',
        language: '',
        abstraction: '',
        delayedRecall: '',
        orientation: '',
        total: '',
        interpretation: ''
      }
    },
    hearingEvaluation: {
      rightEar: { '250Hz': '', '500Hz': '', '1kHz': '', '2kHz': '', '4kHz': '' },
      leftEar: { '250Hz': '', '500Hz': '', '1kHz': '', '2kHz': '', '4kHz': '' }
    },
    recommendation: '',
    duration: '',
    underMedication: '',
    difficultyPerceiving: '',
    tinnitus: '',
    tinnitusLocation: '',
    vertigo: '',
    vertigoType: '',
    noiseExposure: '',
    hearingAidUsage: '',
    usagePattern: '',
    usageHours: ''
  });
  const [recommendations, setRecommendations] = useState({
    audiological: false,
    psychological: false,
    medical: false,
    HAT: false,
    SpeechEvaluation: false,
    Ent:false,
    Neurology:false,
    SpeechLanguageTherapy:false
  });
  // State for systemic illnesses
  const [systemicIllnessList, setSystemicIllnessList] = useState([
    'Diabetes',
    'Hypertension',
    'Cardiovascular disease',
    'Ischemic heart disease',
    'Vascular dementia',
    'Multiple sclerosis',
    'Stroke',
    'Chronic Kidney disease',
    'Parkinson',
    'others'
  ]);

  const [newIllness, setNewIllness] = useState('');

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

  const handleDownloadConfirm = async () => {
    const correctKey = "Varun@6361";

    // Validate key entered in modal
    if (password !== correctKey) {
      alert("Incorrect key! Download canceled.");
      setPassword('');
      setOpenPasswordModal(false);
      return;
    }

    setOpenPasswordModal(false);
    setLoading(true);
    setSaveStatus({
      message: `Requesting Hearing Wellness Clinic ${downloadFormat}...`,
      severity: "info",
    });

    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            table: "hearing_wellness_clinic",
            formName: "Hearing_Wellness_Clinic",
            format: downloadFormat,
            key: password   // 🔥 send KEY instead of password
          }),
        }
      );

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setSaveStatus({
          message: `Failed: ${body.message || resp.statusText}`,
          severity: "error",
        });
        return;
      }

      const blob = await resp.blob();
      const disposition = resp.headers.get("content-disposition") || "";

      let filename = `Hearing_Wellness_Clinic_${Date.now()}.${downloadFormat === "excel" ? "xlsx" : "pdf"
        }`;

      const match = /filename="?([^"]+)"?/.exec(disposition);
      if (match && match[1]) filename = match[1];

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSaveStatus({
        message: `Download started: ${filename}`,
        severity: "success",
      });

    } catch (err) {
      console.error("Download error:", err);
      setSaveStatus({
        message: `Error downloading file: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
      setPassword('');
    }
  };


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };
  // Handle nested form changes
  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Handle MOCA sub-scores
  const handleMocaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      questionnaires: {
        ...prev.questionnaires,
        moca: {
          ...prev.questionnaires.moca,
          [field]: value
        }
      }
    }));
  };

  // Handle hearing evaluation changes
  const handleHearingChange = (ear, freq, value) => {
    setFormData(prev => ({
      ...prev,
      hearingEvaluation: {
        ...prev.hearingEvaluation,
        [ear]: {
          ...prev.hearingEvaluation[ear],
          [freq]: value
        }
      }
    }));
  };

  // Handle systemic illness checkboxes
  const handleSystemicIllness = (illness) => (e) => {
    const newIllnesses = e.target.checked
      ? [...formData.systemicIllnesses, illness]
      : formData.systemicIllnesses.filter(item => item !== illness);

    setFormData(prev => ({ ...prev, systemicIllnesses: newIllnesses }));
  };

  // Add new systemic illness
  const addNewIllness = () => {
    if (newIllness.trim() && !systemicIllnessList.includes(newIllness)) {
      setSystemicIllnessList(prev => [...prev, newIllness.trim()]);
      setNewIllness('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        dateOfAssessment: date,
        formData,
        recommendations
      };

      console.log('Submitting data:', submitData);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hearing-wellness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const result = await response.json();
        alert('Hearing Wellness Evaluation submitted successfully!');

        // Reset form after successful submission
        setDate('');
        setFormData({
          name: '',
          age: '',
          gender: '',
          phone: '',
          opNumber: '',
          occupation: '',
          education: '',
          maritalStatus: '',
          address: '',
          area: '',
          socialization: '',
          socioEconomicStatus: '',
          systemicIllnesses: [],
          selfReportedHearingDifficulties: '',
          hearingLoss: '',
          difficultyPerceiving: '',
          tinnitus: '',
          tinnitusLocation: '',
          vertigo: '',
          vertigoType: '',
          noiseExposure: '',
          hearingAidUsage: '',
          usagePattern: '',
          usageHours: '',
          duration: '', // Hearing aid duration
          underMedication: '',
          questionnaires: {
            hcaq: { result: '', score: '' },
            scap: { result: '', score: '' },
            mmse: { result: '', score: '' },
            moca: {
              visospatial: '',
              naming: '',
              memory: '',
              attention: '',
              language: '',
              abstraction: '',
              delayedRecall: '',
              orientation: '',
              total: '',
              interpretation: ''
            }
          },
          hearingEvaluation: {
            rightEar: { '250Hz': '', '500Hz': '', '1kHz': '', '2kHz': '', '4kHz': '' },
            leftEar: { '250Hz': '', '500Hz': '', '1kHz': '', '2kHz': '', '4kHz': '' }
          }
        });
        setRecommendations({
          audiological: false,
          psychological: false,
          medical: false,
          HAT: false,
          speech: false
        });
      } else {
        const errorData = await response.json();
        alert(`Failed to submit: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting hearing wellness evaluation');
    }
  };
  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 1200, margin: 'auto', mt: 4, mb: 6 }}>
      <Box sx={{ position: 'absolute', top: 220, right: 250 }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadClick}
          disabled={loading}
          sx={{
            borderRadius: 2,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          Download
        </Button>
      </Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
        Geriatric Hearing Wellness Clinic
      </Typography>

      {/* Demographic Data Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Demographic Data
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Age"
              name="age"
              type="number"
              value={formData.age}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                label="Gender"
                displayEmpty
                renderValue={value => value || "Select Gender"}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="OP Number"
              name="opNumber"
              value={formData.opNumber}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Education</InputLabel>
              <Select
                name="education"
                value={formData.education}
                onChange={handleChange}
                label="Education"
                displayEmpty
                renderValue={value => value || "Select Education"}
              >
                <MenuItem value="Primary">Primary</MenuItem>
                <MenuItem value="Higher primary">Higher primary</MenuItem>
                <MenuItem value="Secondary">Secondary</MenuItem>
                <MenuItem value="Pre University">Pre University</MenuItem>
                <MenuItem value="Graduate">Graduate</MenuItem>
                <MenuItem value="Post Graduate">Post Graduate</MenuItem>
                <MenuItem value="Illiterate">Illiterate</MenuItem>
                <MenuItem value="Diploma">Diploma</MenuItem>
                <MenuItem value="Ph.D">Ph.D</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Marital Status</InputLabel>
              <Select
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={handleChange}
                label="Marital Status"
                displayEmpty
                renderValue={value => value || "Select Marital Status"}
              >
                <MenuItem value="Married">Married</MenuItem>
                <MenuItem value="Not married">Not married</MenuItem>
                <MenuItem value="Widower">Widower</MenuItem>
                <MenuItem value="Widow">Widow</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Area of Residence/Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Area</InputLabel>
              <Select
                name="area"
                value={formData.area}
                onChange={handleChange}
                label="Area"
                displayEmpty
                renderValue={value => value || "Select Area"}
              >
                <MenuItem value="Rural">Rural</MenuItem>
                <MenuItem value="Urban">Urban</MenuItem>
                <MenuItem value="Semi urban">Semi urban</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="socialization-label" shrink={true}>Socialization</InputLabel>
              <Select
                labelId="socialization-label"
                id="socialization"
                name="socialization"
                value={formData.socialization ?? ''}
                onChange={handleChange}
                label="Socialization"
                displayEmpty
                renderValue={value => value || "Select Socialization"}
              >
                {/* empty value is optional — remove if you want a forced selection */}
                <MenuItem value=""><em>None</em></MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Socio-economic Status</InputLabel>
              <Select
                name="socioEconomicStatus"
                value={formData.socioEconomicStatus}
                onChange={handleChange}
                label="Socio-economic Status"
                displayEmpty
                renderValue={value => value || "Select Socio-economic Status"}
              >
                <MenuItem value="Upper class">Upper class</MenuItem>
                <MenuItem value="Upper middle class">Upper middle class</MenuItem>
                <MenuItem value="Lower middle class">Lower middle class</MenuItem>
                <MenuItem value="Upper lower class">Upper lower class</MenuItem>
                <MenuItem value="Lower class">Lower class</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Box mb={4} border={1} borderColor="divider" p={2} borderRadius={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="dateOfAssessment"
            label="Date of Assessment"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Box>
      {/* Systemic Illness Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Systemic Illness
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Select applicable illnesses:
            </Typography>
            <Grid container spacing={2}>
              {systemicIllnessList.map((illness) => (
                <Grid item xs={12} sm={6} md={4} key={illness}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.systemicIllnesses.includes(illness)}
                        onChange={handleSystemicIllness(illness)}
                      />
                    }
                    label={illness}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Under Medication</InputLabel>
              <Select
                name="underMedication"
                value={formData.underMedication}
                onChange={handleChange}
                label="Under Medication"
                displayEmpty
                renderValue={value => value || "Select Under Medication"}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Self Report Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Self Report
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Self Reported Hearing Difficulties</InputLabel>
              <Select
                name="selfReportedHearingDifficulties"
                value={formData.selfReportedHearingDifficulties}
                onChange={handleChange}
                label="Self Reported Hearing Difficulties"
                displayEmpty
                renderValue={value => value || "Select Self Reported Hearing Difficulties"}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Hearing Loss</InputLabel>
              <Select
                name="hearingLoss"
                value={formData.hearingLoss}
                onChange={handleChange}
                label="Hearing Loss"
                displayEmpty
                renderValue={value => value || "Select Hearing Loss"}
              >
                <MenuItem value="Right">Right</MenuItem>
                <MenuItem value="Left">Left</MenuItem>
                <MenuItem value="Both">Both</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Difficulty Perceiving Speech in Noise</InputLabel>
              <Select
                name="difficultyPerceiving"
                value={formData.difficultyPerceiving}
                onChange={handleChange}
                label="Difficulty Perceiving Speech in Noise"
                displayEmpty
                renderValue={value => value || "Select Difficulty Perceiving Speech in Noise"}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Tinnitus</InputLabel>
              <Select
                name="tinnitus"
                value={formData.tinnitus}
                onChange={handleChange}
                label="Tinnitus"
                displayEmpty
                renderValue={value => value || "Select Tinnitus"}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.tinnitus === 'Yes' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink={true}>Tinnitus Location</InputLabel>
                <Select
                  name="tinnitusLocation"
                  value={formData.tinnitusLocation}
                  onChange={handleChange}
                  label="Tinnitus Location"
                  displayEmpty
                  renderValue={value => value || "Select Tinnitus Location"}
                >
                  <MenuItem value="Right">Right</MenuItem>
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Both">Both</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Vertigo</InputLabel>
              <Select
                name="vertigo"
                value={formData.vertigo}
                onChange={handleChange}
                label="Vertigo"
                displayEmpty
                renderValue={value => value || "Select Vertigo"}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.vertigo === 'Yes' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink={true}>Vertigo Type</InputLabel>
                <Select
                  name="vertigoType"
                  value={formData.vertigoType}
                  onChange={handleChange}
                  label="Vertigo Type"
                  displayEmpty
                  renderValue={value => value || "Select Vertigo Type"}
                >
                  <MenuItem value="Subjective">Subjective</MenuItem>
                  <MenuItem value="Objective">Objective</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Exposure to Noise</InputLabel>
              <Select
                name="noiseExposure"
                value={formData.noiseExposure}
                onChange={handleChange}
                label="Exposure to Noise"
                displayEmpty
                renderValue={value => value || "Select Exposure to Noise"}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Hearing Aid Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Hearing Aid
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel shrink={true}>Hearing Aid Usage</InputLabel>
              <Select
                name="hearingAidUsage"
                value={formData.hearingAidUsage}
                onChange={handleChange}
                label="Hearing Aid Usage"
                displayEmpty
                renderValue={value => value || "Select Hearing Aid Usage"}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.hearingAidUsage === 'Yes' && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Pattern of Hearing Aid Usage</InputLabel>
                  <Select
                    name="usagePattern"
                    value={formData.usagePattern}
                    onChange={handleChange}
                    label="Pattern of Hearing Aid Usage"
                    displayEmpty
                    renderValue={value => value || "Select Pattern of Hearing Aid Usage"}
                  >
                    <MenuItem value="Full time">Full time</MenuItem>
                    <MenuItem value="Part time">Part time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Average Hours of Usage"
                  name="usageHours"
                  type="number"
                  value={formData.usageHours}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Duration of Usage (in years)"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Questionnaires Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Questionnaires
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1">Hearing Conditioning App Questionnaire</Typography>

            <FormControl fullWidth>
              <InputLabel shrink={true}>Result</InputLabel>
              <Select
                value={formData.questionnaires.hcaq.result}
                onChange={(e) => handleNestedChange('questionnaires', 'hcaq', {
                  ...formData.questionnaires.hcaq,
                  result: e.target.value
                })}
                label="Result"
                displayEmpty
                renderValue={value => value || "Select Result"}
              >
                <MenuItem value="Refer">Refer</MenuItem>
                <MenuItem value="Pass">Pass</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1">SCAP-A</Typography>
            <TextField
              fullWidth
              label="Score"
              value={formData.questionnaires.scap.score}
              onChange={(e) => handleNestedChange('questionnaires', 'scap', {
                ...formData.questionnaires.scap,
                score: e.target.value
              })}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel shrink={true}>Result</InputLabel>
              <Select
                value={formData.questionnaires.scap.result}
                onChange={(e) => handleNestedChange('questionnaires', 'scap', {
                  ...formData.questionnaires.scap,
                  result: e.target.value
                })}
                label="Result"
                displayEmpty
                renderValue={value => value || "Select Result"}
              >
                <MenuItem value="Refer">Refer</MenuItem>
                <MenuItem value="Pass">Pass</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1">MMSE</Typography>
            <TextField
              fullWidth
              label="Score"
              value={formData.questionnaires.mmse.score}
              onChange={(e) => handleNestedChange('questionnaires', 'mmse', {
                ...formData.questionnaires.mmse,
                score: e.target.value
              })}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel shrink={true}>Result</InputLabel>
              <Select
                value={formData.questionnaires.mmse.result}
                onChange={(e) => handleNestedChange('questionnaires', 'mmse', {
                  ...formData.questionnaires.mmse,
                  result: e.target.value
                })}
                label="Result"
                displayEmpty
                renderValue={value => value || "Select Result"}
              >
                <MenuItem value="Refer">Refer</MenuItem>
                <MenuItem value="Pass">Pass</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* MOCA Questionnaire */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              MOCA Questionnaire
            </Typography>
            <Grid container spacing={3}>
              {['visospatial', 'naming', 'memory', 'attention', 'language', 'abstraction', 'delayedRecall', 'orientation'].map((section) => (
                <Grid item xs={12} sm={6} md={3} key={section}>
                  <TextField
                    fullWidth
                    label={section.charAt(0).toUpperCase() + section.slice(1)}
                    value={formData.questionnaires.moca[section]}
                    onChange={(e) => handleMocaChange(section, e.target.value)}
                    variant="outlined"
                  />
                </Grid>
              ))}

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Total Score"
                  value={formData.questionnaires.moca.total}
                  onChange={(e) => handleMocaChange('total', e.target.value)}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Interpretation</InputLabel>
                  <Select
                    value={formData.questionnaires.moca.interpretation}
                    onChange={(e) => handleMocaChange('interpretation', e.target.value)}
                    label="Interpretation"
                    displayEmpty
                    renderValue={value => value || "Select Interpretation"}
                  >
                    <MenuItem value="Normal Cognition (26-30)">Normal Cognition (26-30)</MenuItem>
                    <MenuItem value="Mild Cognitive Impairment (18-25)">Mild Cognitive Impairment (18-25)</MenuItem>
                    <MenuItem value="Moderate Cognitive Impairment (10-17)">Moderate Cognitive Impairment (10-17)</MenuItem>
                    <MenuItem value="Severe Cognitive Impairment (<10)">Severe Cognitive Impairment (&lt;10)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Hearing Evaluation Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Hearing Evaluation
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ear</TableCell>
                <TableCell align="center">250Hz</TableCell>
                <TableCell align="center">500Hz</TableCell>
                <TableCell align="center">1kHz</TableCell>
                <TableCell align="center">2kHz</TableCell>
                <TableCell align="center">4kHz</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">Right Ear</TableCell>
                {['250Hz', '500Hz', '1kHz', '2kHz', '4kHz'].map((freq) => (
                  <TableCell key={`right-${freq}`} align="center">
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      value={formData.hearingEvaluation.rightEar[freq]}
                      onChange={(e) => handleHearingChange('rightEar', freq, e.target.value)}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">Left Ear</TableCell>
                {['250Hz', '500Hz', '1kHz', '2kHz', '4kHz'].map((freq) => (
                  <TableCell key={`left-${freq}`} align="center">
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      value={formData.hearingEvaluation.leftEar[freq]}
                      onChange={(e) => handleHearingChange('leftEar', freq, e.target.value)}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold" color="secondary">
          Recommendations
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.audiological}
                  onChange={() => setRecommendations({ ...recommendations, audiological: !recommendations.audiological })}
                  color="primary"
                />
              }
              label="Audiological Evaluation"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.psychological}
                  onChange={() => setRecommendations({ ...recommendations, psychological: !recommendations.psychological })}
                  color="primary"
                />
              }
              label="Psychological Evaluation"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.medical}
                  onChange={() => setRecommendations({ ...recommendations, medical: !recommendations.medical })}
                  color="primary"
                />
              }
              label="Medical Evaluation"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.HAT}
                  onChange={() => setRecommendations({ ...recommendations, HAT: !recommendations.HAT })}
                  color="primary"
                />
              }
              label="Hearing Aid Trial"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.speech}
                  onChange={() => setRecommendations({ ...recommendations, speech: !recommendations.speech })}
                  color="primary"
                />
              }
              label="Speech Evaluation"
            />
          </Grid>
            <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.speechLanguageTherapy}
                  onChange={() => setRecommendations({ ...recommendations, speechLanguageTherapy: !recommendations.speechLanguageTherapy })}
                  color="primary"
                />
              }
              label="Speech Language Therapy"
            />
          </Grid>
            <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.Ent}
                  onChange={() => setRecommendations({ ...recommendations, Ent: !recommendations.Ent })}
                  color="primary"
                />
              }
              label="ENT Specialist"

            />
          </Grid>
            <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recommendations.Neurology}
                  onChange={() => setRecommendations({ ...recommendations, Neurology: !recommendations.Neurology })}
                  color="primary"
                />
              }
              label="Neurology Evaluation"
            />
          </Grid>
        </Grid>
      </Paper>
      <Dialog
        open={openPasswordModal}
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDownloadConfirm}
            variant="contained"
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
      {/* Submit Button */}
      <Box display="flex" justifyContent="center" mt={4}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ px: 6, py: 1.5, fontSize: '1.1rem' }}
          onClick={handleSubmit}
        >
          Submit Evaluation
        </Button>
      </Box>
    </Paper>
  );
};

export default HearingWellness;